import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

interface IPLocationData {
  ip: string;
  country: string;
  countryCode: string;
  city: string;
  region: string;
  timezone: string;
  isp?: string;
  org?: string;
}

/**
 * Get the user's real public IP address and location data
 * Uses Firebase Function to avoid CORS issues and rate limiting
 */
export const getIPLocationData = async (): Promise<IPLocationData> => {
  try {
    const functions = getFunctions(getApp());
    const getIPLocation = httpsCallable(functions, 'getIPLocation');
    
    const result = await getIPLocation();
    const response = result.data as { success: boolean; data: IPLocationData; error?: string };
    
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.error || 'Failed to get location data');
    }
  } catch (error) {
    console.error('Failed to get IP location data:', error);
    
    // Fallback to basic IP detection
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      if (ipResponse.ok) {
        const ipData = await ipResponse.json();
        return {
          ip: ipData.ip || 'Unknown',
          country: 'Unknown',
          countryCode: 'Unknown',
          city: 'Unknown',
          region: 'Unknown',
          timezone: 'Unknown',
          isp: 'Unknown',
          org: 'Unknown'
        };
      }
    } catch (ipError) {
      console.error('Failed to get IP address:', ipError);
    }
    
    // Final fallback
    return {
      ip: 'Unknown',
      country: 'Unknown',
      countryCode: 'Unknown',
      city: 'Unknown',
      region: 'Unknown',
      timezone: 'Unknown',
      isp: 'Unknown',
      org: 'Unknown'
    };
  }
};

/**
 * Get just the IP address (faster, for when location isn't needed)
 */
export const getPublicIP = async (): Promise<string> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    if (response.ok) {
      const data = await response.json();
      return data.ip || 'Unknown';
    }
  } catch (error) {
    console.error('Failed to get public IP:', error);
  }
  return 'Unknown';
};

/**
 * Format location data for display
 */
export const formatLocation = (locationData: IPLocationData): string => {
  if (locationData.city === 'Unknown' && locationData.country === 'Unknown') {
    return 'Location Unknown';
  }
  
  const parts = [];
  if (locationData.city && locationData.city !== 'Unknown') {
    parts.push(locationData.city);
  }
  if (locationData.region && locationData.region !== 'Unknown' && locationData.region !== locationData.city) {
    parts.push(locationData.region);
  }
  if (locationData.country && locationData.country !== 'Unknown') {
    parts.push(locationData.country);
  }
  
  return parts.length > 0 ? parts.join(', ') : 'Location Unknown';
};

/**
 * Format location data from AdminActivity (without ip field)
 */
export const formatLocationFromActivity = (locationData: {
  country: string;
  countryCode: string;
  city: string;
  region: string;
  timezone: string;
  isp?: string;
  org?: string;
}): string => {
  if (locationData.city === 'Unknown' && locationData.country === 'Unknown') {
    return 'Location Unknown';
  }
  
  const parts = [];
  if (locationData.city && locationData.city !== 'Unknown') {
    parts.push(locationData.city);
  }
  if (locationData.region && locationData.region !== 'Unknown' && locationData.region !== locationData.city) {
    parts.push(locationData.region);
  }
  if (locationData.country && locationData.country !== 'Unknown') {
    parts.push(locationData.country);
  }
  
  return parts.length > 0 ? parts.join(', ') : 'Location Unknown';
}; 